import AppCopy from "copy/AppCopy";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { BiSolidMessageRoundedDetail } from "react-icons/bi";
import ChatCard from "components/card/ChatCard";
import { IoChevronDown } from "react-icons/io5";
import { MessageListProps } from "declarations/messages";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";
import useChat from "hook/useChat";
import { SocketEvents, getUserRoom } from "config/socket";
import { upsertConversation } from "reducers/chatReducer";
import {
  subscribeToSocketEvent,
  syncSocketRoom,
} from "utils/socketClient";

function MessagesList({ onSelectChat }: MessageListProps) {
  const { getConversations } = useChat();
  const dispatch = useDispatch();
  const { chat, conversations } = useSelector((state: RootState) => state.chat);
  const { user } = useSelector((state: RootState) => state.user);
  const [loadingConversations, setLoadingConversations] =
    useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [loadError, setLoadError] = useState<boolean>(false);
  const conversationIdsRef = useRef<Set<string>>(new Set());
  const loadDataRef = useRef<() => Promise<void>>(async () => undefined);

  const loadData = async () => {
    setLoadError(false);
    try {
      await getConversations(
        { setLoading: setLoadingConversations },
        { reload: true }
      );
    } catch {
      setLoadError(true);
    }
  };
  loadDataRef.current = loadData;

  useEffect(() => {
    void loadDataRef.current();
  }, []);

  useEffect(() => {
    conversationIdsRef.current = new Set(
      conversations.map((conversation) => String(conversation._id))
    );
  }, [conversations]);

  useEffect(() => {
    if (!user?._id) return;

    const unsubscribeUserRoom = syncSocketRoom(getUserRoom(user._id));
    const unsubscribeConversationUpdated = subscribeToSocketEvent<{
      conversationId: string;
      lastMessage: any;
      unreadCounts: {
        customer?: number;
        staff?: number;
        admin?: number;
      };
    }>(SocketEvents.ConversationUpdated, (payload) => {
      if (!conversationIdsRef.current.has(payload.conversationId)) {
        void loadDataRef.current();
        return;
      }

      dispatch(
        upsertConversation({
          _id: payload.conversationId,
          lastMessage: payload.lastMessage,
          unreadCounts: {
            customer: payload.unreadCounts?.customer || 0,
            staff: payload.unreadCounts?.staff || 0,
            admin: payload.unreadCounts?.admin || 0,
          },
        })
      );
    });
    const unsubscribeConversationRead = subscribeToSocketEvent<{
      conversationId: string;
      unreadCounts: {
        customer?: number;
        staff?: number;
        admin?: number;
      };
    }>(SocketEvents.ConversationRead, (payload) => {
      dispatch(
        upsertConversation({
          _id: payload.conversationId,
          unreadCounts: {
            customer: payload.unreadCounts?.customer || 0,
            staff: payload.unreadCounts?.staff || 0,
            admin: payload.unreadCounts?.admin || 0,
          },
          moveToTop: false,
        })
      );
    });

    return () => {
      unsubscribeConversationUpdated();
      unsubscribeConversationRead();
      unsubscribeUserRoom();
    };
  }, [dispatch, user?._id]);

  const handleOnSearchValueChange = (e: any) => {
    setSearchValue(e.target.value);
  };

  const filteredConversations = useMemo(() => {
    if (!searchValue) return conversations;

    return conversations.filter((conversation) => {
      const customerInfo = [
        conversation.customer?.name,
        conversation.customer?.phoneNumber,
        conversation.customer?._id,
        conversation.lastMessage?.text,
        conversation.car?.make?.name || conversation.car?.make,
        conversation.car?.model,
        conversation.car?.year,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return customerInfo.includes(searchValue.toLowerCase());
    });
  }, [conversations, searchValue]);

  const handleLoadMoreConversations = async () => {
    try {
      await getConversations(
        { offset: conversations.length, setLoading: setLoadingMore },
        { more: true }
      );
    } catch {
      setLoadError(true);
    }
  };

  const handleSelectChat = () => {
    onSelectChat && onSelectChat();
  };

  const { messagesSearchPlaceholder, messagesTitle, title } =
    AppCopy.messagesPage;

  return (
    <div className={`${ClassName.container} ${chat && ClassName.mobileOnly}`}>
      <span className={ClassName.title}>{title}</span>
      <div className={ClassName.searchBar}>
        <input
          type="text"
          className={ClassName.searchInput}
          placeholder={messagesSearchPlaceholder}
          value={searchValue}
          onChange={handleOnSearchValueChange}
        />
        <CiSearch size={23} aria-hidden="true" />
      </div>

      <div className={ClassName.listTitle}>
        <BiSolidMessageRoundedDetail size={14} />
        <span>{messagesTitle}</span>
      </div>

      {loadingConversations && conversations.length === 0 ? (
        <div className={ClassName.loadingState}>
          <div className="loader"></div>
        </div>
      ) : loadError && conversations.length === 0 ? (
        <div className={ClassName.emptyState}>
          <span className={ClassName.emptyStateTitle}>Messages could not be loaded</span>
          <span className={ClassName.emptyStateText}>
            Check the connection and try again.
          </span>
          <button type="button" className={ClassName.retryButton} onClick={loadData}>
            Try again
          </button>
        </div>
      ) : filteredConversations.length === 0 ? (
        <div className={ClassName.emptyState}>
          <span className={ClassName.emptyStateTitle}>
            No conversations found
          </span>
          <span className={ClassName.emptyStateText}>
            Try a different search or wait for a new message to arrive.
          </span>
        </div>
      ) : (
        <div className={ClassName.listContainer}>
          {filteredConversations?.map((conversation) => (
            <ChatCard
              onSelectChat={handleSelectChat}
              key={conversation._id}
              conversation={conversation}
            />
          ))}
          {conversations &&
            conversations.length > 19 &&
            !loadingMore && (
              <div className={ClassName.loadModeButtonContainer}>
                <div
                  className={ClassName.loadModeButton}
                  onClick={handleLoadMoreConversations}
                >
                  <IoChevronDown size={20} />
                </div>
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export default MessagesList;

const ClassName = {
  container: "flex flex-col w-full sm:w-64 lg:w-72",
  mobileOnly: "hidden sm:flex",
  title: "font-bold text-3xl",
  searchBar:
    "h-11 w-full flex items-center px-3 rounded-xl bg-slate-50 border border-slate-200 mt-4 gap-3 focus-within:border-slate-400",
  searchInput: "flex flex-1 min-w-0 bg-transparent outline-none text-sm",
  listTitle: "flex items-center gap-3 text-sm mt-6 text-secondaryText mb-4",
  listContainer:
    "flex min-h-0 w-full flex-1 flex-col overflow-y-auto",
  loadingState:
    "flex min-h-56 w-full flex-1 items-center justify-center rounded-2xl border border-slate-100 bg-white",
  emptyState:
    "flex min-h-56 w-full flex-1 flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 text-center",
  emptyStateTitle: "text-sm font-semibold text-primaryText",
  emptyStateText: "text-xs leading-6 text-secondaryText max-w-[240px]",
  retryButton:
    "mt-2 rounded-xl bg-primaryText px-4 py-2 text-xs font-semibold text-white",
  loadModeButtonContainer: "w-full flex items-center justify-center",
  loadModeButton: "p-4 cursor-pointer",
};
